<template>
  <div>
    <div class="mb-4">
      <h4 class="fw-bold mb-0"><i class="fas fa-sliders-h me-2 text-primary"></i>Settings</h4>
      <p class="text-muted small mb-0 mt-1">
        Effective configuration of the running system.
      </p>
    </div>

    <!-- Read-only banner -->
    <div class="alert alert-info d-flex align-items-center">
      <i class="fas fa-lock me-2"></i>
      <div>
        Settings are configured via environment and applied on deploy. This page is read-only.
        The <router-link to="/admin/kill-switch" class="alert-link">kill switch</router-link> is the
        only live runtime control.
      </div>
    </div>

    <!-- Loading -->
    <div v-if="loading" class="text-center py-5">
      <div class="spinner-border text-primary" role="status"></div>
      <p class="text-muted mt-3">Loading settings…</p>
    </div>

    <!-- Page-level error -->
    <div v-else-if="pageError" class="alert alert-danger">
      <i class="fas fa-exclamation-triangle me-2"></i>
      <strong>Failed to load settings:</strong> {{ pageError }}
    </div>

    <!-- Settings grouped by category -->
    <template v-else>
      <div
        v-for="category in categories"
        :key="category"
        class="card border-0 shadow-sm mb-4"
      >
        <div class="card-header bg-white border-bottom py-3">
          <h6 class="fw-semibold mb-0 text-dark">
            <i :class="categoryIcon(category)" class="me-2"></i>{{ category }}
          </h6>
        </div>
        <div class="card-body p-0">
          <div
            v-for="(setting, index) in settingsByCategory[category]"
            :key="setting.key"
            :class="['px-4 py-3', index < settingsByCategory[category].length - 1 ? 'border-bottom' : '']"
          >
            <div class="row align-items-center g-3">
              <!-- Key + type -->
              <div class="col-lg-4">
                <span class="fw-semibold text-dark font-monospace small">{{ setting.key }}</span>
                <div class="text-muted" style="font-size: 0.72rem;">
                  Type: {{ setting.type }}
                </div>
              </div>

              <!-- Effective value + source badge -->
              <div class="col-lg-5">
                <div class="d-flex align-items-center gap-2">
                  <code class="setting-value">{{ formatValue(setting.value) }}</code>
                  <span
                    :class="['badge', setting.source === 'env' ? 'bg-primary' : 'bg-secondary']"
                    :title="setting.source === 'env'
                      ? 'Value set via environment variable (.env)'
                      : 'Environment variable unset — using the hardcoded default'"
                  >
                    {{ setting.source }}
                  </span>
                </div>
                <div v-if="setting.source === 'env'" style="font-size: 0.72rem; color: #adb5bd;">
                  Default: {{ formatValue(setting.default) }}
                </div>
              </div>

              <!-- Help -->
              <div class="col-lg-3 text-lg-end">
                <button
                  class="btn btn-link btn-sm p-0 text-muted"
                  @click="openHelp(setting)"
                  title="What does this setting do?"
                >
                  <i class="fas fa-question-circle me-1"></i>Details
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </template>

    <!-- Help modal -->
    <SettingsHelpModal
      :visible="helpModal.visible"
      :title="helpModal.title"
      :description="helpModal.description"
      @close="helpModal.visible = false"
    />
  </div>
</template>

<script>
import { fetchAllSettings } from '../services/settingsApi.js';
import SettingsHelpModal from '../components/SettingsHelpModal.vue';

const CATEGORY_ICONS = {
  'Market Hours':           'fas fa-clock text-info',
  'Risk & Sizing':          'fas fa-shield-alt text-danger',
  'Position Management':    'fas fa-tasks text-warning',
  'Researcher & Filtering': 'fas fa-filter text-primary',
  'Technical Analysis':     'fas fa-chart-line text-success',
};

export default {
  name: 'SettingsView',
  components: { SettingsHelpModal },

  data() {
    return {
      loading: true,
      pageError: null,
      settings: [],
      helpModal: {
        visible: false,
        title: '',
        description: '',
      },
    };
  },

  computed: {
    categories() {
      const seen = new Set();
      const order = ['Risk & Sizing', 'Position Management', 'Market Hours', 'Researcher & Filtering', 'Technical Analysis'];
      const cats = [];
      for (const cat of order) {
        if (this.settings.some(s => s.category === cat)) {
          cats.push(cat);
          seen.add(cat);
        }
      }
      // Any categories not in the fixed order
      for (const s of this.settings) {
        if (!seen.has(s.category)) {
          cats.push(s.category);
          seen.add(s.category);
        }
      }
      return cats;
    },

    settingsByCategory() {
      const groups = {};
      for (const s of this.settings) {
        if (!groups[s.category]) groups[s.category] = [];
        groups[s.category].push(s);
      }
      return groups;
    },
  },

  async mounted() {
    await this.loadSettings();
  },

  methods: {
    async loadSettings() {
      this.loading = true;
      this.pageError = null;
      try {
        const settings = await fetchAllSettings();
        this.settings = settings;

        const cats = [...new Set(settings.map(s => s.category))];
        console.log(`[SettingsView] Loaded ${settings.length} settings. Categories: ${cats.join(', ')}`);
      } catch (err) {
        this.pageError = err.detail || err.message;
        console.error('[SettingsView] Failed to load settings:', err);
      } finally {
        this.loading = false;
      }
    },

    formatValue(value) {
      if (value === null || value === undefined || value === '') return '—';
      return String(value);
    },

    categoryIcon(category) {
      return CATEGORY_ICONS[category] || 'fas fa-cog text-secondary';
    },

    openHelp(setting) {
      this.helpModal.title = setting.key;
      this.helpModal.description = setting.description;
      this.helpModal.visible = true;
    },
  },
};
</script>

<style scoped>
.setting-value {
  background: #f8f9fa;
  border: 1px solid #e9ecef;
  border-radius: 4px;
  padding: 2px 8px;
  color: #212529;
  word-break: break-all;
}
</style>
